import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import api from '../services/api';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter, 
  MoreVertical,
  UserPlus,
  Eye,
  EyeOff,
  Check,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timezones, setTimezones] = useState([]);
  const [loadingTimezones, setLoadingTimezones] = useState(false);

  // Log quando os estados mudam
  useEffect(() => {
    console.log('🔄 Estado departments atualizado:', departments);
    console.log('🔄 Estado positions atualizado:', positions);
  }, [departments, positions]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    fullName: '',
    password: '',
    role: 'common_user',
    phone: '',
    address: '',
    bio: '',
    companyId: '',
    departmentId: '',
    positionId: '',
    timezone: -3
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Verificar se o usuário é administrador
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Acesso negado. Apenas administradores podem acessar esta página.');
      // Redirecionar para dashboard ou página de erro
      return;
    }
  }, [user]);

  // Carregar usuários e roles
  useEffect(() => {
    if (user?.role === 'admin') {
      loadUsers();
      loadRoles();
      loadCompanies();
      loadTimezones();
    }
  }, [user]);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      
      // Verificar se a resposta tem success ou se tem users diretamente
      if (response.data.success || response.data.users) {
        const data = response.data;
        const usersList = data.users || [];
        console.log('📊 Resposta da API /users:', data);
        console.log('👥 Usuários retornados:', usersList);
        if (usersList.length > 0) {
          console.log('👤 Primeiro usuário:', usersList[0]);
          console.log('📱 Campos do perfil:', {
            bio: usersList[0].bio,
            phone: usersList[0].phone,
            address: usersList[0].address
          });
        }
        setUsers(usersList);
      } else {
        toast.error(`Erro ao carregar usuários: ${response.data.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      if (error.response) {
        toast.error(`Erro ao carregar usuários: ${error.response.data?.error || 'Erro desconhecido'}`);
      } else {
        toast.error('Erro ao carregar usuários');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await api.get('/users/roles');
      
      // Verificar se a resposta tem success ou se tem roles diretamente
      if (response.data.success || response.data.roles) {
        const data = response.data;
        setRoles(data.roles || []);
      } else {
        console.error('Erro ao carregar roles:', response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar roles:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await api.get('/organizational/companies');
      
      // Verificar se a resposta tem success ou se tem companies diretamente
      if (response.data.success || response.data.companies) {
        const data = response.data;
        setCompanies(data.companies || []);
      } else {
        console.error('Erro ao carregar empresas:', response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  };

  const loadTimezones = async () => {
    try {
      setLoadingTimezones(true);
      const response = await api.get('/users/timezones');
      if (response.data.success) {
        setTimezones(response.data.timezones);
      }
    } catch (error) {
      console.error('Erro ao carregar timezones:', error);
    } finally {
      setLoadingTimezones(false);
    }
  };

  const loadDepartments = async (companyId) => {
    if (!companyId) {
      setDepartments([]);
      return;
    }
    
    try {
      console.log(`📡 Carregando departamentos para empresa ${companyId}...`);
      const response = await api.get(`/organizational/companies/${companyId}/departments`);
      
      console.log(`📡 Resposta departamentos:`, response.data);
      
      // Verificar se a resposta tem success ou se tem departments diretamente
      if (response.data.success || response.data.departments) {
        const data = response.data;
        const departmentsList = data.departments || [];
        console.log(`🏢 Departamentos carregados:`, departmentsList);
        setDepartments(departmentsList);
      } else {
        console.error('Erro ao carregar departamentos:', response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
    }
  };

  const loadPositions = async (companyId) => {
    if (!companyId) {
      setPositions([]);
      return;
    }
    
    try {
      console.log(`📡 Carregando cargos para empresa ${companyId}...`);
      const response = await api.get(`/organizational/companies/${companyId}/positions`);
      
      console.log(`📡 Resposta cargos:`, response.data);
      
      // Verificar se a resposta tem success ou se tem positions diretamente
      if (response.data.success || response.data.positions) {
        const data = response.data;
        const positionsList = data.positions || [];
        console.log(`💼 Cargos carregados:`, positionsList);
        setPositions(positionsList);
      } else {
        console.error('Erro ao carregar cargos:', response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Validação
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = 'Username é obrigatório';
    if (formData.username.trim() && !/^[a-zA-Z0-9_]+$/.test(formData.username.trim())) {
      newErrors.username = 'Username deve conter apenas letras, números e underscore';
    }
    if (!formData.email.trim()) newErrors.email = 'Email é obrigatório';
    if (!formData.fullName.trim()) newErrors.fullName = 'Nome completo é obrigatório';
    if (!formData.password) newErrors.password = 'Senha é obrigatória';
    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Senha deve ter pelo menos 8 caracteres';
    }
    if (formData.password && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.password)) {
      newErrors.password = 'Senha deve conter pelo menos uma letra minúscula, uma maiúscula, um número e um caractere especial';
    }
    if (!formData.companyId) newErrors.companyId = 'Empresa é obrigatória';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      // Transformar dados do frontend para o formato esperado pelo backend
      const backendData = {
        ...formData,
        company_id: parseInt(formData.companyId),
        department_id: formData.departmentId ? parseInt(formData.departmentId) : null,
        position_id: formData.positionId ? parseInt(formData.positionId) : null
      };
      
      // Remover campos camelCase que não são esperados pelo backend
      delete backendData.companyId;
      delete backendData.departmentId;
      delete backendData.positionId;
      
      // Converter telefone formatado para apenas números antes de enviar
      if (backendData.phone) {
        backendData.phone = backendData.phone.replace(/\D/g, "");
      }
      
      // Log para mostrar que apenas números estão sendo enviados no telefone
      console.log('📱 Telefone formatado visualmente:', formData.phone);
      console.log('📱 Telefone enviado para backend (apenas números):', backendData.phone);

      const response = await api.post('/users', backendData);

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success('Usuário criado com sucesso!');
        setShowCreateModal(false);
        resetForm();
        loadUsers();
      } else {
        toast.error(response.data.error || 'Erro ao criar usuário');
      }
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      toast.error('Erro ao criar usuário');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    
    // Validação
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = 'Username é obrigatório';
    if (formData.username.trim() && !/^[a-zA-Z0-9_]+$/.test(formData.username.trim())) {
      newErrors.username = 'Username deve conter apenas letras, números e underscore';
    }
    if (!formData.email.trim()) newErrors.email = 'Email é obrigatório';
    if (!formData.fullName.trim()) newErrors.fullName = 'Nome completo é obrigatório';
    if (!formData.companyId) newErrors.companyId = 'Empresa é obrigatória';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      // Transformar dados do frontend para o formato esperado pelo backend
      const backendData = {
        ...formData,
        company_id: parseInt(formData.companyId),
        department_id: formData.departmentId ? parseInt(formData.departmentId) : null,
        position_id: formData.positionId ? parseInt(formData.positionId) : null
      };
      
      // Remover campos camelCase que não são esperados pelo backend
      delete backendData.companyId;
      delete backendData.departmentId;
      delete backendData.positionId;
      
      // Converter telefone formatado para apenas números antes de enviar
      if (backendData.phone) {
        backendData.phone = backendData.phone.replace(/\D/g, "");
      }
      
      // Log para mostrar que apenas números estão sendo enviados no telefone
      console.log('📱 Telefone formatado visualmente:', formData.phone);
      console.log('📱 Telefone enviado para backend (apenas números):', backendData.phone);

      const response = await api.put(`/users/${editingUser.id}`, backendData);

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success('Usuário atualizado com sucesso!');
        setShowEditModal(false);
        setEditingUser(null);
        resetForm();
        loadUsers();
      } else {
        toast.error(response.data.error || 'Erro ao atualizar usuário');
      }
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      toast.error('Erro ao atualizar usuário');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) {
      return;
    }

    try {
      const response = await api.delete(`/users/${userId}`);

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success('Usuário excluído com sucesso!');
        loadUsers();
      } else {
        toast.error(response.data.error || 'Erro ao excluir usuário');
      }
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error('Erro ao excluir usuário');
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const response = await api.patch(`/users/${userId}/toggle-status`);

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success(`Usuário ${currentStatus ? 'desativado' : 'ativado'} com sucesso!`);
        loadUsers();
      } else {
        toast.error(response.data.error || 'Erro ao alterar status do usuário');
      }
    } catch (error) {
      console.error('Erro ao alterar status do usuário:', error);
      toast.error('Erro ao alterar status do usuário');
    }
  };

  const openEditModal = (user) => {
    console.log('🔍 Abrindo modal de edição para usuário:', user);
    setEditingUser(user);
    
    // Mapear campos do usuário para o formulário
    const formData = {
      username: user.username,
      email: user.email,
      fullName: user.fullName,
      password: '',
      role: user.role,
      phone: user.phone || '',
      address: user.address || '',
      bio: user.bio || '',
      companyId: user.company_id || user.companyId || '',
      departmentId: user.department_id || user.departmentId || '',
      positionId: user.position_id || user.positionId || '',
      timezone: user.timezone || -3
    };
    
    // Aplicar máscara visual no telefone se existir
    if (formData.phone) {
      formData.phone = formatPhone(formData.phone);
    }
    
    console.log('📝 Dados do formulário mapeados:', formData);
    setFormData(formData);
    
    // Carregar departamentos e cargos da empresa do usuário
    if (formData.companyId) {
      console.log('🏢 Carregando departamentos e cargos para empresa:', formData.companyId);
      loadDepartments(formData.companyId);
      loadPositions(formData.companyId);
    } else {
      console.log('⚠️  Usuário não tem empresa associada');
    }
    
    setShowEditModal(true);
    setErrors({});
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    setEditingUser(null);
    resetForm();
    setErrors({});
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      fullName: '',
      password: '',
      role: 'common_user',
      phone: '',
      address: '',
      bio: '',
      companyId: '',
      departmentId: '',
      positionId: '',
      timezone: -3
    });
    setErrors({});
    
    // Limpar departamentos e cargos
    setDepartments([]);
    setPositions([]);
  };

  // Função para formatar telefone para exibição visual
  const formatPhone = (value) => {
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '');
    
    // Aplica máscara baseada no número de dígitos
    if (numbers.length === 11) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    } else if (numbers.length === 10) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    } else if (numbers.length > 11) {
      // Limita a 11 dígitos
      const limited = numbers.slice(0, 11);
      return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7)}`;
    }
    
    // Retorna apenas os números se não atingir o formato
    return numbers;
  };

  // Função para obter apenas números do telefone
  const getPhoneNumbersOnly = (value) => {
    return value.replace(/\D/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Para telefone: aplicar máscara visual mas armazenar apenas números
    if (name === 'phone') {
      // Aplicar máscara visual no campo
      const maskedValue = formatPhone(value);
      e.target.value = maskedValue;
      
      // Armazenar apenas os números no estado
      const numbersOnly = value.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: numbersOnly
      }));
    } else {
      // Para outros campos, armazenar o valor normalmente
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Carregar departamentos e cargos quando a empresa for alterada
    if (name === 'companyId') {
      loadDepartments(value);
      loadPositions(value);
      // Limpar departamento e cargo selecionados
      setFormData(prev => ({
        ...prev,
        departmentId: '',
        positionId: ''
      }));
    }
    
    // Limpar cargo quando o departamento for alterado
    if (name === 'departmentId') {
      setFormData(prev => ({
        ...prev,
        positionId: ''
      }));
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.fullName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !filterRole || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Acesso Negado</h1>
          <p className="text-gray-600">Apenas administradores podem acessar esta página.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <Layout>
      <div>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Gerenciamento de Usuários</h1>
          <p className="text-gray-600">Crie, edite e gerencie usuários do sistema</p>
        </div>
        
        {/* Filtros e Busca */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Buscar usuários..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todas as funções</option>
                {roles.map(role => (
                  <option key={role.id} value={role.name}>{role.display_name}</option>
                ))}
              </select>
              <button
                onClick={openCreateModal}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <UserPlus className="h-4 w-4" />
                Criar Usuário
              </button>
            </div>
          </div>
        </div>

        {/* Lista de Usuários */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuário
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Função
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organização
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Último Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criado em
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((userItem) => {
                  return (
                    <tr key={userItem.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                          {userItem.avatar_base64 ? (
                            <img 
                              src={userItem.avatar_base64} 
                              alt="Avatar" 
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-sm font-medium text-gray-600">
                              {userItem.fullName?.charAt(0) || userItem.username?.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{userItem.fullName}</div>
                          <div className="text-sm text-gray-500">{userItem.email}</div>
                          <div className="text-sm text-gray-400">@{userItem.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        userItem.role === 'admin' ? 'bg-red-100 text-red-800' :
                        userItem.role === 'manager' ? 'bg-yellow-100 text-yellow-800' :
                        userItem.role === 'viewer' ? 'bg-primary-100 text-primary-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {userItem.role === 'admin' ? 'Administrador' :
                         userItem.role === 'manager' ? 'Gerente' :
                         userItem.role === 'viewer' ? 'Visualizador' :
                         'Usuário Comum'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="font-medium">{userItem.companyName || 'N/A'}</div>
                        <div className="text-gray-500">
                          {userItem.departmentName && `${userItem.departmentName} • `}
                          {userItem.positionName || 'Sem cargo'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        userItem.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {userItem.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {userItem.lastLogin ? 
                        new Date(userItem.lastLogin).toLocaleString('pt-BR', {
                          year: 'numeric', 
                          month: '2-digit', 
                          day: '2-digit',
                          hour: '2-digit', 
                          minute: '2-digit'
                        }) : 'Nunca'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(userItem.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleToggleStatus(userItem.id, userItem.isActive)}
                          className={`px-3 py-1 text-xs rounded-md transition-colors ${
                            userItem.isActive 
                              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                          disabled={userItem.id === user.id} // Não permitir desativar próprio usuário
                        >
                          {userItem.isActive ? 'Desativar' : 'Ativar'}
                        </button>
                        <button
                          onClick={() => openEditModal(userItem)}
                          className="text-primary-600 hover:text-primary-900 transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(userItem.id)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          disabled={userItem.id === user.id} // Não permitir excluir próprio usuário
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
          
          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <UserPlus className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum usuário encontrado</h3>
              <p className="text-gray-500">
                {searchTerm || filterRole ? 'Tente ajustar os filtros de busca.' : 'Comece criando o primeiro usuário.'}
              </p>
            </div>
          )}
        </div>

        {/* Modal de Criação */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Criar Novo Usuário</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={handleCreateUser} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.username ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.username && <p className="text-red-500 text-sm mt-1">{errors.username}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.fullName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Senha *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.password ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                  <p className="text-xs text-gray-500 mt-1">
                    Mínimo 8 caracteres, com letra maiúscula, minúscula, número e caractere especial
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Função
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {roles.map(role => (
                      <option key={role.id} value={role.name}>{role.display_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Empresa *
                  </label>
                  <select
                    name="companyId"
                    value={formData.companyId}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Selecione uma empresa</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Departamento
                  </label>
                  <select
                    name="departmentId"
                    value={formData.departmentId}
                    onChange={handleInputChange}
                    disabled={!formData.companyId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="">Selecione um departamento</option>
                    {departments.map(department => (
                      <option key={department.id} value={department.id}>{department.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cargo
                  </label>
                  <select
                    name="positionId"
                    value={formData.positionId}
                    onChange={handleInputChange}
                    disabled={!formData.companyId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="">Selecione um cargo</option>
                    {positions.map(position => (
                      <option key={position.id} value={position.id}>{position.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timezone
                  </label>
                  <select
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {timezones.map(timezone => (
                      <option key={timezone.value} value={timezone.value}>
                        {timezone.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(XX) XXXXX-XXXX ou (XX) XXXX-XXXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Biografia
                  </label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Criar Usuário
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Edição */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Editar Usuário</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={handleEditUser} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.username ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.username && <p className="text-red-500 text-sm mt-1">{errors.username}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.fullName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nova Senha (deixe em branco para manter a atual)
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Nova senha (opcional)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {formData.password && (
                    <p className="text-xs text-gray-500 mt-1">
                      Mínimo 8 caracteres, com letra maiúscula, minúscula, número e caractere especial
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Função
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {roles.map(role => (
                      <option key={role.id} value={role.name}>{role.display_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Empresa *
                  </label>
                  <select
                    name="companyId"
                    value={formData.companyId}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Selecione uma empresa</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Departamento
                  </label>
                  <select
                    name="departmentId"
                    value={formData.departmentId}
                    onChange={handleInputChange}
                    disabled={!formData.companyId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="">Selecione um departamento</option>
                    {departments.map(department => (
                      <option key={department.id} value={department.id}>{department.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cargo
                  </label>
                  <select
                    name="positionId"
                    value={formData.positionId}
                    onChange={handleInputChange}
                    disabled={!formData.companyId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="">Selecione um cargo</option>
                    {positions.map(position => (
                      <option key={position.id} value={position.id}>{position.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timezone
                  </label>
                  <select
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {timezones.map(timezone => (
                      <option key={timezone.value} value={timezone.value}>
                        {timezone.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(XX) XXXXX-XXXX ou (XX) XXXX-XXXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Biografia
                  </label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Salvar Alterações
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserManagement;
